/**
 * TaxEfficientPositioning - Tax-Aware Portfolio Management
 * TASK-206 Phase 3: Advanced Features
 *
 * Identifies tax loss harvesting opportunities, gain coordination strategies,
 * and dividend timing recommendations across client portfolios
 */

import React, { useState } from 'react';
import { DollarSign, TrendingDown, TrendingUp, Calendar, AlertCircle, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';
import Badge from './atoms/Badge';

export interface TaxLossHarvestingOpportunity {
  id: string;
  security: string;
  isin: string;
  portfolio: string;
  currentValue: number;
  costBasis: number;
  unrealizedLoss: number;
  lossPercentage: number;
  potentialTaxSavings: number;
  replacement: string;
  replacementIsin: string;
  correlationWithReplacement: number;
  rationale: string;
  washSaleRisk: 'LOW' | 'MEDIUM' | 'HIGH';
}

export interface GainCoordinationStrategy {
  id: string;
  security: string;
  portfolios: Array<{
    name: string;
    value: number;
    unrealizedGain: number;
    gainPercentage: number;
  }>;
  totalGain: number;
  recommendation: string;
  taxYear: number;
  estimatedTaxImpact: number;
  rationale: string;
}

export interface DividendTimingRecommendation {
  id: string;
  security: string;
  portfolio: string;
  exDividendDate: string;
  estimatedDividend: number;
  withholdingTax: number;
  recommendation: 'BUY_BEFORE' | 'BUY_AFTER' | 'HOLD' | 'SELL_BEFORE';
  rationale: string;
}

export interface TaxEfficientPositioningProps {
  clientId: string;
  taxLossOpportunities: TaxLossHarvestingOpportunity[];
  gainCoordinationStrategies: GainCoordinationStrategy[];
  dividendRecommendations: DividendTimingRecommendation[];
  estimatedTotalTaxSavings: number;
  className?: string;
}

const TaxEfficientPositioning: React.FC<TaxEfficientPositioningProps> = ({
  clientId: _clientId,
  taxLossOpportunities,
  gainCoordinationStrategies,
  dividendRecommendations,
  estimatedTotalTaxSavings,
  className = ''
}) => {
  const [activeTab, setActiveTab] = useState<'losses' | 'gains' | 'dividends'>('losses');
  const [expandedLoss, setExpandedLoss] = useState<string | null>(null);

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `CHF ${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `CHF ${(value / 1000).toFixed(0)}K`;
    }
    return `CHF ${value.toLocaleString('de-CH')}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-CH', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getWashSaleBadge = (risk: string) => {
    switch (risk) {
      case 'HIGH':
        return <Badge variant="error" size="sm" label="HIGH WASH RISK" />;
      case 'MEDIUM':
        return <Badge variant="warning" size="sm" label="MEDIUM RISK" />;
      case 'LOW':
        return <Badge variant="success" size="sm" label="LOW RISK" />;
      default:
        return null;
    }
  };

  const getRecommendationBadge = (rec: string) => {
    switch (rec) {
      case 'BUY_BEFORE':
        return <Badge variant="success" size="sm" label="BUY BEFORE" />;
      case 'BUY_AFTER':
        return <Badge variant="default" size="sm" label="BUY AFTER" />;
      case 'HOLD':
        return <Badge variant="warning" size="sm" label="HOLD" />;
      case 'SELL_BEFORE':
        return <Badge variant="error" size="sm" label="SELL BEFORE" />;
      default:
        return null;
    }
  };

  const toggleLoss = (lossId: string) => {
    setExpandedLoss(expandedLoss === lossId ? null : lossId);
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
            <DollarSign className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Tax-Efficient Positioning</h3>
            <p className="text-sm text-gray-600">Maximize after-tax returns through strategic tax management</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(estimatedTotalTaxSavings)}
          </div>
          <p className="text-xs text-gray-500">Estimated tax savings</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          <button
            onClick={() => setActiveTab('losses')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'losses'
                ? 'border-green-600 text-green-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Loss Harvesting ({taxLossOpportunities.length})
          </button>
          <button
            onClick={() => setActiveTab('gains')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'gains'
                ? 'border-green-600 text-green-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Gain Coordination ({gainCoordinationStrategies.length})
          </button>
          <button
            onClick={() => setActiveTab('dividends')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'dividends'
                ? 'border-green-600 text-green-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Dividend Timing ({dividendRecommendations.length})
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {/* Loss Harvesting Tab */}
        {activeTab === 'losses' && (
          <div className="space-y-4">
            {taxLossOpportunities.length > 0 ? (
              taxLossOpportunities.map((opp) => {
                const isExpanded = expandedLoss === opp.id;

                return (
                  <div
                    key={opp.id}
                    className="bg-white rounded-lg border-2 border-green-200 shadow-sm"
                  >
                    {/* Opportunity Header - Clickable */}
                    <button
                      onClick={() => toggleLoss(opp.id)}
                      className="w-full px-6 py-4 flex items-start justify-between hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start gap-4 flex-1">
                        <div className="mt-1">
                          <TrendingDown className="w-5 h-5 text-green-600" />
                        </div>

                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-base font-semibold text-gray-900">
                              {opp.security}
                            </h4>
                            {getWashSaleBadge(opp.washSaleRisk)}
                          </div>

                          <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                            <div>
                              <span className="text-gray-500">Portfolio:</span>
                              <span className="ml-2 font-medium text-gray-900">{opp.portfolio}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">ISIN:</span>
                              <span className="ml-2 font-mono text-xs text-gray-700">{opp.isin}</span>
                            </div>
                          </div>

                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Current Value:</span>
                              <p className="font-semibold text-gray-900">{formatCurrency(opp.currentValue)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Unrealized Loss:</span>
                              <p className="font-bold text-red-600">
                                {formatCurrency(opp.unrealizedLoss)} ({opp.lossPercentage.toFixed(1)}%)
                              </p>
                            </div>
                            <div>
                              <span className="text-gray-500">Tax Savings:</span>
                              <p className="font-bold text-green-600">{formatCurrency(opp.potentialTaxSavings)}</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="ml-4">
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </button>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-6 pb-6 border-t border-gray-200">
                        <div className="mt-4 space-y-4">
                          {/* Replacement Security */}
                          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <h5 className="text-sm font-semibold text-blue-900 mb-3">Replacement Security</h5>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-blue-700">Security:</span>
                                <p className="font-semibold text-blue-900 mt-1">{opp.replacement}</p>
                              </div>
                              <div>
                                <span className="text-blue-700">ISIN:</span>
                                <p className="font-mono text-xs text-blue-800 mt-1">{opp.replacementIsin}</p>
                              </div>
                              <div>
                                <span className="text-blue-700">Correlation:</span>
                                <p className="font-semibold text-blue-900 mt-1">
                                  {(opp.correlationWithReplacement * 100).toFixed(0)}%
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Rationale */}
                          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                            <h5 className="text-sm font-semibold text-gray-900 mb-2">Tax Strategy Rationale</h5>
                            <p className="text-sm text-gray-700">{opp.rationale}</p>
                          </div>

                          {/* Wash Sale Warning */}
                          {opp.washSaleRisk !== 'LOW' && (
                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <div className="flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-yellow-800">
                                  <strong>Wash Sale Warning:</strong> Ensure no purchase of substantially identical securities within 30 days before or after this sale to maintain tax deductibility.
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h4 className="text-lg font-semibold text-gray-700 mb-2">
                  No Tax Loss Harvesting Opportunities
                </h4>
                <p className="text-sm text-gray-600">
                  No positions with unrealized losses suitable for tax harvesting at this time.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Gain Coordination Tab */}
        {activeTab === 'gains' && (
          <div className="space-y-4">
            {gainCoordinationStrategies.length > 0 ? (
              gainCoordinationStrategies.map((strategy) => (
                <div
                  key={strategy.id}
                  className="bg-white rounded-lg border-2 border-yellow-200 p-6 shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1">
                      <TrendingUp className="w-5 h-5 text-yellow-600" />
                    </div>

                    <div className="flex-1">
                      <h4 className="text-base font-semibold text-gray-900 mb-3">
                        {strategy.security}
                      </h4>

                      {/* Portfolio Breakdown */}
                      <div className="mb-4 space-y-2">
                        {strategy.portfolios.map((pf, idx) => (
                          <div key={idx} className="flex items-center justify-between text-sm p-2 bg-gray-50 rounded">
                            <span className="text-gray-700">{pf.name}</span>
                            <div className="flex items-center gap-4">
                              <span className="text-gray-600">{formatCurrency(pf.value)}</span>
                              <span className="font-semibold text-green-600">
                                +{formatCurrency(pf.unrealizedGain)} ({pf.gainPercentage.toFixed(1)}%)
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Total Gain & Tax Impact */}
                      <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div>
                          <span className="text-sm text-yellow-700">Total Unrealized Gain:</span>
                          <p className="text-lg font-bold text-yellow-900">{formatCurrency(strategy.totalGain)}</p>
                        </div>
                        <div>
                          <span className="text-sm text-yellow-700">Tax Year:</span>
                          <p className="text-lg font-bold text-yellow-900">{strategy.taxYear}</p>
                        </div>
                        <div>
                          <span className="text-sm text-yellow-700">Estimated Tax:</span>
                          <p className="text-lg font-bold text-red-700">{formatCurrency(strategy.estimatedTaxImpact)}</p>
                        </div>
                      </div>

                      {/* Recommendation */}
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <h5 className="text-sm font-semibold text-blue-900 mb-2">Recommendation</h5>
                        <p className="text-sm text-blue-800 mb-3">{strategy.recommendation}</p>
                        <p className="text-xs text-blue-700 italic">{strategy.rationale}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h4 className="text-lg font-semibold text-gray-700 mb-2">
                  No Gain Coordination Needed
                </h4>
                <p className="text-sm text-gray-600">
                  No positions requiring gain coordination across portfolios at this time.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Dividend Timing Tab */}
        {activeTab === 'dividends' && (
          <div className="space-y-4">
            {dividendRecommendations.length > 0 ? (
              dividendRecommendations.map((div) => (
                <div
                  key={div.id}
                  className="bg-white rounded-lg border-2 border-purple-200 p-6 shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    <div className="mt-1">
                      <Calendar className="w-5 h-5 text-purple-600" />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-base font-semibold text-gray-900">
                          {div.security}
                        </h4>
                        {getRecommendationBadge(div.recommendation)}
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                        <div>
                          <span className="text-gray-500">Portfolio:</span>
                          <span className="ml-2 font-medium text-gray-900">{div.portfolio}</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Ex-Dividend Date:</span>
                          <span className="ml-2 font-semibold text-purple-700">{formatDate(div.exDividendDate)}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                        <div>
                          <span className="text-sm text-purple-700">Estimated Dividend:</span>
                          <p className="text-lg font-bold text-purple-900">{formatCurrency(div.estimatedDividend)}</p>
                        </div>
                        <div>
                          <span className="text-sm text-purple-700">Withholding Tax:</span>
                          <p className="text-lg font-bold text-red-700">{formatCurrency(div.withholdingTax)}</p>
                        </div>
                      </div>

                      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <p className="text-sm text-gray-700">{div.rationale}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                <CheckCircle2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h4 className="text-lg font-semibold text-gray-700 mb-2">
                  No Upcoming Dividend Events
                </h4>
                <p className="text-sm text-gray-600">
                  No dividend-related timing recommendations for the next 60 days.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer Summary */}
      <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-800">
            <strong>Tax Optimization Summary:</strong> Implementing these strategies could save approximately{' '}
            <strong>{formatCurrency(estimatedTotalTaxSavings)}</strong> in taxes while maintaining similar market exposure.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TaxEfficientPositioning;
